import { create } from 'zustand';
import type { CurrentUser, LoginCredentials } from '@/shared/types/index';
import { authService } from '@/shared/api/auth.service';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: CurrentUser | null;
  /** Restore the session on app boot via GET /api/auth/me. */
  bootstrap: () => Promise<void>;
  /** POST /api/auth/login then load the full profile via /me. */
  login: (credentials: LoginCredentials) => Promise<void>;
  /** POST /api/auth/logout and clear local state. */
  logout: () => Promise<void>;
  /** Local-only clear (used by the 401 interceptor). */
  clearSession: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'idle',
  user: null,

  async bootstrap() {
    set({ status: 'loading' });
    try {
      const user = await authService.me();
      set({ status: 'authenticated', user });
    } catch {
      set({ status: 'unauthenticated', user: null });
    }
  },

  async login(credentials) {
    // login() sets the cookie; /me returns the full profile (incl. department).
    await authService.login(credentials);
    const user = await authService.me();
    set({ status: 'authenticated', user });
  },

  async logout() {
    try {
      await authService.logout();
    } finally {
      set({ status: 'unauthenticated', user: null });
    }
  },

  clearSession() {
    set({ status: 'unauthenticated', user: null });
  },
}));
